/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState } from 'react';
import { Button, useFlags, HeroImageCombined, Text, Box } from 'sarsaparilla';

import LotteryScheduleHeader from './LotteryScheduleHeader';
import LotteryScheduleTable from './LotteryScheduleTable';
import InfoModal from '../../../shared-components/common/InfoModal';
import { type DailyLottery, type Permit } from '../../utils/lottery-schedule-utils';

type PropTypes = {
    permitID?: string;
    permit: Permit;
    recarea: any;
    permitError: string;
    recareaError: string;
    dailyLotteries: DailyLottery[];
    dailyLotteriesError: string;
};

export default function LotterySchedule({
    permitID,
    permit,
    recarea,
    permitError,
    recareaError,
    dailyLotteriesError,
    dailyLotteries,
}: PropTypes) {
    const { enableLotteryTransparency } = useFlags();

    const [statusModalOpen, setStatusModalOpen] = useState(false);

    const onStatusModalOpen = () => setStatusModalOpen(true);

    const onStatusModalClose = () => setStatusModalOpen(false);

    const renderHeader = () => {
        return (
            <div className="lottery-schedule-header">
                <LotteryScheduleHeader
                    permitID={permitID}
                    permit={permit}
                    recarea={recarea}
                    permitError={permitError}
                    recareaError={recareaError}
                    dailyLotteriesError={dailyLotteriesError}
                />
            </div>
        );
    };

    const renderDescription = () => {
        return (
            <div className="lottery-schedule-description">
                {enableLotteryTransparency ? (
                    <Button appearance="link" href="/lottery/available">
                        Lottery Statistics and Information
                    </Button>
                ) : (
                    <Button appearance="link" href="/lottery/how-they-work">
                        How Does the Lottery Work?
                    </Button>
                )}
            </div>
        );
    };

    const renderTable = () => {
        return (
            <div className="lottery-schedule-table">
                <LotteryScheduleTable
                    permit={permit}
                    dailyLotteries={dailyLotteries}
                    onStatusModalClicked={onStatusModalOpen}
                />
            </div>
        );
    };

    const renderModal = () => {
        return (
            <InfoModal
                isOpen={statusModalOpen}
                closeModal={onStatusModalClose}
                title="What Do The Statuses Mean?"
            >
                <div>
                    <Text fontWeight="bold">Coming Soon</Text>
                    <Text>The lottery hasn&apos;t open yet.</Text>
                    <Text fontWeight="bold">Open</Text>
                    <Text>You can now apply for this lottery.</Text>
                </div>
            </InfoModal>
        );
    };

    return (
        <div data-testid="lottery-schedule" className="lottery-schedule-page">
            <div
                className="permit-hero-wrap"
                aria-label="Hero images"
                data-testid="permit-venue-header"
            >
                <HeroImageCombined type="asset" id={permitID} />
            </div>

            <div className="permit-content-wrap">
                <Box padding="md">
                    {renderHeader()}
                    {renderDescription()}
                    {renderTable()}
                    {renderModal()}
                </Box>
            </div>
        </div>
    );
}
